import { useCallback, useEffect, useRef, useState } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { motion, useScroll, useSpring } from 'framer-motion';
import { GAMES } from '@/lib/gameConfig';
import { ChallengeSlip } from './components/ChallengeSlip';
import { WaitlistForm } from './components/WaitlistForm';
import { CountUp, MaskedLines, Reveal, SectionLabel } from './components/primitives';
import './landing.css';

/* ------------------------------------------------------------------ data */

const LIVE_GAMES = GAMES.filter(
  (g, i, arr) => !g.comingSoon && arr.findIndex((x) => x.name === g.name) === i
);

const STEPS = [
  {
    n: '01',
    title: 'Set the terms',
    body: 'Pick the game, name your stake, send the link. Public to anyone, or private to the one person who said they were better.',
  },
  {
    n: '02',
    title: 'Play the match',
    body: 'Both sides fund the escrow, then you play on the platform you already play on. Nothing to install, no lobby to babysit.',
  },
  {
    n: '03',
    title: 'Get paid',
    body: "The result comes from the game's own API. The contract pays the winner. There is no step where anyone has to be trusted.",
  },
];

const ESCROW = [
  {
    tag: 'T+0',
    title: 'Both sides fund',
    body: 'Each player sends their stake to the escrow contract. Until both have, either side can walk and take theirs straight back.',
  },
  {
    tag: 'Locked',
    title: 'The contract holds it',
    body: 'Stakes are frozen on Base. PullUp holds no key that can move them, and neither does your opponent. Now go play.',
  },
  {
    tag: 'Final',
    title: 'The API calls it',
    body: "We read the finished match from the game's official API and submit that verified result on-chain. No screenshots enter this process.",
  },
  {
    tag: 'Paid',
    title: 'The pot releases',
    body: 'Winner takes the pot less the 1% fee, straight to their wallet. Typically inside a minute of the match ending.',
  },
];

const FINE_PRINT = [
  {
    n: '01',
    title: 'Verified, not reported',
    body: "Results are read from the game's own API. Nobody uploads a screenshot, nobody self-reports, nobody gets to relitigate the last round.",
  },
  {
    n: '02',
    title: 'Non-custodial by design',
    body: 'Stakes live in a smart contract on Base, not in a PullUp balance. We could disappear tomorrow and your money would still be exactly where you left it.',
  },
  {
    n: '03',
    title: 'Settlement in seconds',
    body: 'The moment a result is confirmed, the payout fires. No pending period, no withdrawal queue, no three-to-five business days.',
  },
  {
    n: '04',
    title: 'No house, no odds',
    body: "There's no line to beat and nobody taking the other side of your bet. You're playing the person who accepted, for their money, at your skill.",
  },
];

const COMPARE = {
  cols: ['PullUp', 'The group chat', 'A sportsbook'],
  rows: [
    ['Who holds the money', 'A smart contract', 'Whoever offered first', 'The house'],
    ['Who calls the winner', "The game's own API", 'Whoever argues longest', 'The house'],
    ['Time to get paid', 'Under 60 seconds', 'When they feel like it', '1–5 business days'],
    ['What it costs', '1% of the pot', 'A friendship, eventually', 'The vig, every time'],
    ["What you're betting on", 'Your own skill', 'Your own skill', "Somebody else's"],
  ],
};

/* -------------------------------------------------------------- fragments */

function Nav({ onLogin, onWaitlist }: { onLogin: () => void; onWaitlist: () => void }) {
  const [solid, setSolid] = useState(false);

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className="sticky top-0 z-50 transition-colors duration-300"
      style={{
        background: solid ? 'rgba(8,9,10,0.82)' : 'transparent',
        backdropFilter: solid ? 'blur(14px)' : 'none',
        borderBottom: `1px solid ${solid ? 'var(--line)' : 'transparent'}`,
      }}
    >
      <div className="pu-shell flex h-[68px] items-center justify-between">
        <a href="#top" className="flex items-baseline gap-3">
          <span
            className="pu-display text-[19px]"
            style={{ letterSpacing: '-0.02em', fontStretch: '88%' }}
          >
            PullUp
          </span>
          <span className="pu-label hidden sm:inline">Skill wagering</span>
        </a>

        <nav className="flex items-center gap-1 sm:gap-6">
          <a
            href="#how"
            className="pu-label hidden transition-colors hover:text-[var(--ink)] md:inline"
          >
            How it works
          </a>
          <a
            href="#escrow"
            className="pu-label hidden transition-colors hover:text-[var(--ink)] md:inline"
          >
            The escrow
          </a>
          <button
            type="button"
            onClick={onLogin}
            className="pu-label transition-colors hover:text-[var(--ink)]"
          >
            Sign in
          </button>
          <button type="button" onClick={onWaitlist} className="pu-btn pu-btn--sm ml-3 sm:ml-5">
            <span>Get an invite</span>
          </button>
        </nav>
      </div>
    </header>
  );
}

function StatCell({
  value,
  label,
  accent,
}: {
  value: React.ReactNode;
  label: string;
  accent?: boolean;
}) {
  return (
    <div className="py-5" style={{ borderTop: '1px solid var(--line-2)' }}>
      <div
        className="pu-num text-[clamp(26px,3vw,38px)] font-semibold leading-none"
        style={{ color: accent ? 'var(--acid)' : 'var(--ink)' }}
      >
        {value}
      </div>
      <div className="pu-label mt-3">{label}</div>
    </div>
  );
}

function Ticker() {
  const names = LIVE_GAMES.map((g) => g.name);
  const run = [...names, ...names];

  return (
    <section
      className="pu-marquee-wrap overflow-hidden py-7"
      style={{ borderTop: '1px solid var(--line)', background: 'var(--surface)' }}
      aria-label="Supported games"
    >
      <div className="pu-marquee" style={{ ['--dur' as string]: '58s' }}>
        {run.map((name, i) => (
          <span key={i} className="flex shrink-0 items-center">
            <span
              className="pu-display px-6 text-[clamp(18px,2vw,26px)]"
              style={{ color: i % 2 === 0 ? 'var(--ink)' : 'var(--ink-3)' }}
            >
              {name}
            </span>
            <span aria-hidden style={{ color: 'var(--acid)' }} className="pu-num text-[11px]">
              ◆
            </span>
          </span>
        ))}
      </div>
    </section>
  );
}

function EscrowTrack() {
  const trackRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ['start 75%', 'end 65%'],
  });
  const scaleY = useSpring(scrollYProgress, { stiffness: 90, damping: 26, mass: 0.4 });

  return (
    <div ref={trackRef} className="relative mt-14 pl-8 sm:pl-14">
      {/* rail */}
      <div
        className="absolute bottom-0 left-0 top-2 w-px"
        style={{ background: 'var(--line-2)' }}
      />
      <motion.div
        className="absolute left-0 top-2 w-px"
        style={{
          bottom: 0,
          background: 'var(--acid)',
          scaleY,
          transformOrigin: 'top',
        }}
      />

      <div className="space-y-14 sm:space-y-20">
        {ESCROW.map((item, i) => (
          <Reveal key={item.tag} delay={i * 0.04}>
            <div className="relative">
              <span
                className="absolute -left-8 top-[7px] block h-1.5 w-1.5 sm:-left-14"
                style={{ background: 'var(--acid)', marginLeft: -2.5 }}
              />
              <div className="grid gap-3 sm:grid-cols-[110px_1fr] sm:gap-8">
                <div className="pu-label pt-1" style={{ color: 'var(--acid)' }}>
                  {item.tag}
                </div>
                <div>
                  <h3
                    className="pu-display text-[clamp(20px,2.4vw,30px)]"
                    style={{ fontStretch: '90%' }}
                  >
                    {item.title}
                  </h3>
                  <p className="pu-body mt-3 max-w-xl">{item.body}</p>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

function CompareTable() {
  return (
    <div className="mt-12 overflow-x-auto">
      <table className="w-full min-w-[720px] border-collapse text-left">
        <thead>
          <tr>
            <th className="pu-label w-[26%] pb-4 pr-6 align-bottom font-medium">Head to head</th>
            {COMPARE.cols.map((c, i) => (
              <th
                key={c}
                className="pu-display pb-4 pr-6 align-bottom text-[clamp(15px,1.5vw,20px)]"
                style={{
                  color: i === 0 ? 'var(--acid)' : 'var(--ink-3)',
                  fontStretch: '88%',
                }}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {COMPARE.rows.map((row) => (
            <tr key={row[0]}>
              {row.map((cell, i) => (
                <td
                  key={i}
                  className={i === 0 ? 'pu-label py-5 pr-6 align-top' : 'py-5 pr-6 align-top'}
                  style={{
                    borderTop: '1px solid var(--line)',
                    fontSize: i === 0 ? undefined : 15,
                    color: i === 0 ? undefined : i === 1 ? 'var(--ink)' : 'var(--ink-3)',
                    lineHeight: 1.5,
                  }}
                >
                  {i === 1 && (
                    <span aria-hidden className="mr-2" style={{ color: 'var(--acid)' }}>
                      ▸
                    </span>
                  )}
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------- page */

export function LandingPage() {
  const { login } = usePrivy();

  const goToWaitlist = useCallback(() => {
    const section = document.getElementById('waitlist');
    if (!section) return;
    section.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => {
      document.getElementById('waitlist-email')?.focus({ preventScroll: true });
    }, 700);
  }, []);

  return (
    <div className="pu min-h-screen" id="top">
      {/* Column rules + grain, fixed behind everything */}
      <div className="pu-backdrop" aria-hidden>
        <div className="pu-backdrop__cols">
          <i />
          <i />
          <i />
          <i />
        </div>
        <div className="pu-backdrop__grain" />
      </div>

      <div className="pu-layer">
        <Nav onLogin={login} onWaitlist={goToWaitlist} />

        {/* ---------------------------------------------------------- hero */}
        <section className="pu-shell pb-16 pt-12 sm:pt-20 lg:pb-24 lg:pt-24">
          <div className="grid items-start gap-12 lg:grid-cols-[1.15fr_minmax(360px,430px)] lg:gap-16">
            <div className="min-w-0">
              <Reveal y={12} onMount>
                <div className="pu-label flex items-center gap-3">
                  <span className="pu-dot" />
                  <span>Early access · Invite only</span>
                </div>
              </Reveal>

              <h1
                className="pu-display mt-7 text-[clamp(48px,8.6vw,116px)]"
                style={{ fontStretch: '86%' }}
              >
                <MaskedLines
                  onMount
                  delay={0.08}
                  lines={[
                    <span key="think">Think you&rsquo;re</span>,
                    <span key="better">better?</span>,
                    <span key="prove" style={{ color: 'var(--acid)' }}>
                      Prove it.
                    </span>,
                  ]}
                />
              </h1>

              <Reveal delay={0.42} onMount>
                <p className="pu-body mt-8 max-w-[46ch]">
                  Challenge anyone at the game you already play. Both sides put money in, the
                  game&rsquo;s own API calls the winner, and a smart contract pays out. No
                  screenshots. No arguing. No house.
                </p>
              </Reveal>

              <Reveal delay={0.5} onMount>
                <div className="mt-9 flex flex-wrap items-center gap-3">
                  <button type="button" className="pu-btn pu-btn--acid" onClick={goToWaitlist}>
                    <span>Join the waitlist</span>
                    <span aria-hidden>→</span>
                  </button>
                  <button type="button" className="pu-btn" onClick={login}>
                    <span>I have a code</span>
                  </button>
                </div>
              </Reveal>

              {/* stat rail */}
              <div className="mt-14 grid grid-cols-2 gap-x-8 sm:grid-cols-4">
                <Reveal delay={0.58} onMount>
                  <StatCell value={<CountUp to={1} suffix="%" />} label="Platform fee" accent />
                </Reveal>
                <Reveal delay={0.64} onMount>
                  <StatCell value={<CountUp to={60} prefix="<" suffix="s" />} label="Settlement" />
                </Reveal>
                <Reveal delay={0.7} onMount>
                  <StatCell value={<CountUp to={LIVE_GAMES.length} />} label="Games live" />
                </Reveal>
                <Reveal delay={0.76} onMount>
                  <StatCell value="0" label="Trust required" />
                </Reveal>
              </div>
            </div>

            {/* the slip */}
            <Reveal delay={0.24} y={30} onMount>
              <ChallengeSlip onSend={goToWaitlist} />
            </Reveal>
          </div>
        </section>

        <Ticker />

        {/* ------------------------------------------------- how it works */}
        <section id="how" className="pu-shell pu-section" style={{ scrollMarginTop: 68 }}>
          <Reveal>
            <SectionLabel index="01">How it works</SectionLabel>
          </Reveal>

          <Reveal delay={0.05}>
            <h2
              className="pu-display mt-6 max-w-[14ch] text-[clamp(34px,5.6vw,72px)]"
              style={{ fontStretch: '88%' }}
            >
              Three steps. Then you&rsquo;re playing.
            </h2>
          </Reveal>

          <div className="mt-16 grid gap-px sm:grid-cols-3" style={{ background: 'var(--line)' }}>
            {STEPS.map((step, i) => (
              <Reveal key={step.n} delay={i * 0.07}>
                <div className="h-full p-7 sm:p-8" style={{ background: 'var(--bg)' }}>
                  <div
                    className="pu-num text-[clamp(44px,6vw,76px)] font-semibold leading-none"
                    style={{
                      color: 'transparent',
                      WebkitTextStroke: '1px rgba(244, 244, 241, 0.26)',
                    }}
                  >
                    {step.n}
                  </div>
                  <h3
                    className="pu-display mt-7 text-[clamp(20px,2.2vw,26px)]"
                    style={{ fontStretch: '90%' }}
                  >
                    {step.title}
                  </h3>
                  <p className="pu-body mt-3 text-[15px]">{step.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* ----------------------------------------------------- escrow */}
        <section id="escrow" className="pu-shell pu-section" style={{ scrollMarginTop: 68 }}>
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:gap-16">
            <div className="min-w-0 lg:sticky lg:top-28 lg:self-start">
              <Reveal>
                <SectionLabel index="02">Where the money sits</SectionLabel>
              </Reveal>
              <Reveal delay={0.05}>
                <h2
                  className="pu-display mt-6 text-[clamp(34px,5.2vw,66px)]"
                  style={{ fontStretch: '88%' }}
                >
                  Nobody has to be trusted.
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="pu-body mt-6 max-w-[42ch]">
                  Every stake is held by code, not by us. Here is the whole lifecycle of a wager,
                  end to end.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div
                  className="mt-8 inline-flex items-center gap-3 px-4 py-3"
                  style={{ border: '1px solid var(--line-2)' }}
                >
                  <span className="pu-dot" />
                  <span className="pu-num text-[12px]" style={{ color: 'var(--ink-2)' }}>
                    WagerEscrow.sol
                  </span>
                  <span className="pu-label">Base L2 · USDC</span>
                </div>
              </Reveal>
            </div>

            <EscrowTrack />
          </div>
        </section>

        {/* -------------------------------------------------- fine print */}
        <section className="pu-shell pu-section">
          <Reveal>
            <SectionLabel index="03">The fine print</SectionLabel>
          </Reveal>
          <Reveal delay={0.05}>
            <h2
              className="pu-display mt-6 max-w-[16ch] text-[clamp(34px,5.2vw,66px)]"
              style={{ fontStretch: '88%' }}
            >
              Four things that make this different.
            </h2>
          </Reveal>

          <div className="mt-16 grid gap-x-16 sm:grid-cols-2">
            {FINE_PRINT.map((item, i) => (
              <Reveal key={item.n} delay={(i % 2) * 0.07}>
                <div className="py-9" style={{ borderTop: '1px solid var(--line-2)' }}>
                  <div className="pu-label" style={{ color: 'var(--acid)' }}>
                    {item.n}
                  </div>
                  <h3
                    className="pu-display mt-5 text-[clamp(21px,2.4vw,30px)]"
                    style={{ fontStretch: '90%' }}
                  >
                    {item.title}
                  </h3>
                  <p className="pu-body mt-3 max-w-[44ch] text-[15px]">{item.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* --------------------------------------------------- comparison */}
        <section className="pu-shell pu-section">
          <Reveal>
            <SectionLabel index="04">Head to head</SectionLabel>
          </Reveal>
          <Reveal delay={0.05}>
            <h2
              className="pu-display mt-6 max-w-[15ch] text-[clamp(34px,5.2vw,66px)]"
              style={{ fontStretch: '88%' }}
            >
              You already bet on yourself. Badly.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <CompareTable />
          </Reveal>
        </section>

        {/* ---------------------------------------------------- final CTA */}
        <section id="waitlist" className="pu-shell pu-section" style={{ scrollMarginTop: 68 }}>
          <div className="grid gap-12 lg:grid-cols-[1fr_minmax(0,1fr)] lg:gap-20">
            <div className="min-w-0">
              <Reveal>
                <SectionLabel index="05">Get in</SectionLabel>
              </Reveal>
              <h2
                className="pu-display mt-6 text-[clamp(40px,7vw,90px)]"
                style={{ fontStretch: '86%' }}
              >
                <MaskedLines
                  lines={[
                    <span key="rank">Your rank</span>,
                    <span key="doesnt">doesn&rsquo;t</span>,
                    <span key="rent" style={{ color: 'var(--acid)' }}>
                      pay rent.
                    </span>,
                  ]}
                />
              </h2>
              <Reveal delay={0.2}>
                <p className="pu-body mt-7 max-w-[40ch]">
                  PullUp is invite-only while we scale. Leave your email and we&rsquo;ll send a code
                  when a slot opens.
                </p>
              </Reveal>
            </div>

            <Reveal delay={0.1} className="lg:pt-24">
              <WaitlistForm />
            </Reveal>
          </div>
        </section>

        {/* -------------------------------------------------------- footer */}
        <footer style={{ borderTop: '1px solid var(--line)' }}>
          <div className="pu-shell py-14">
            <div className="flex flex-col justify-between gap-10 md:flex-row md:items-end">
              <div
                className="pu-display text-[clamp(56px,13vw,150px)] leading-[0.8]"
                style={{ fontStretch: '84%' }}
              >
                PullUp
              </div>
              <div className="flex flex-col gap-3 md:items-end">
                <div className="flex items-center gap-2.5">
                  <span className="pu-dot" />
                  <span className="pu-label">All systems operational</span>
                </div>
                <a
                  href="https://pullup.gg"
                  className="pu-label transition-colors hover:text-[var(--ink)]"
                >
                  pullup.gg
                </a>
              </div>
            </div>

            <div
              className="mt-12 flex flex-col gap-4 pt-7 md:flex-row md:items-center md:justify-between"
              style={{ borderTop: '1px solid var(--line)' }}
            >
              <p className="pu-label" style={{ letterSpacing: '0.12em' }}>
                © {new Date().getFullYear()} PullUp · Skill-based competition only
              </p>
              <nav className="flex flex-wrap gap-6">
                {['Terms', 'Privacy', 'Responsible play'].map((l) => (
                  <a
                    key={l}
                    href="#top"
                    className="pu-label transition-colors hover:text-[var(--ink)]"
                  >
                    {l}
                  </a>
                ))}
              </nav>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
